/**
 * Converters that turn Medusa entities into GA4 eCommerce items.
 *
 * Converters always return FULL items (product-identifying fields intact).
 * Scrubbing is per-sink:
 * - GA4 dataLayer: use `scrubItemsForDataLayer` before pushing, gated on
 *   analytics (Statistics) consent. In Advanced Consent Mode, GA still sends
 *   cookieless event pings, so item names must be absent without consent.
 * - PostHog / Braze: receive full items and self-gate on their own consent
 *   (PostHog opts out without analytics consent; Braze no-ops without
 *   marketing consent).
 */

import type { HttpTypes } from '@medusajs/types';
import { hasAnalyticsConsent } from './consent';
import type { GA4Item } from './types';

/**
 * Strip product-identifying fields from a GA4 item. Keeps price, quantity,
 * currency, discount, index — aggregate-safe, no condition leak.
 *
 * Deliberate asymmetry: `item_id` and `item_name` are required `string` fields
 * on GA4Item so they are blanked to `''` (empty carries no condition data),
 * while optional identifying fields (`item_category`, `item_brand`,
 * `item_variant`, `item_list_*`) are dropped entirely by omission.
 */
const scrubHealthFields = (item: GA4Item): GA4Item => ({
  item_id: '',
  item_name: '',
  price: item.price,
  quantity: item.quantity,
  currency: item.currency,
  discount: item.discount,
  index: item.index,
});

/**
 * Scrub items for the GA4 dataLayer push when analytics (Statistics) consent
 * is absent. In Advanced Consent Mode GA still sends cookieless event pings,
 * so item names must not be present without consent. PostHog/Braze receive
 * the FULL items elsewhere (they self-gate on their own consent).
 */
export const scrubItemsForDataLayer = (items: GA4Item[]): GA4Item[] => {
  if (hasAnalyticsConsent()) {
    return items;
  }

  return items.map(scrubHealthFields);
};

type GA4ProductInput = {
  id?: string | null;
  title?: string | null;
  collection?: { title?: string | null } | null;
  categories?: Array<{ name?: string | null }> | null;
  variants?: Array<{
    id?: string | null;
    title?: string | null;
    calculated_price?: {
      calculated_amount: number | null;
      original_amount: number | null;
      currency_code: string | null;
    } | null;
  }> | null;
};

export const productToGA4Item = (
  product: GA4ProductInput,
  opts?: {
    index?: number;
    listId?: string;
    listName?: string;
    variantId?: string;
  },
): GA4Item => {
  const variant = opts?.variantId
    ? product.variants?.find((v) => v.id === opts.variantId)
    : product.variants
        ?.filter((v) => !!v.calculated_price)
        .sort(
          (a, b) =>
            (a.calculated_price?.calculated_amount ?? 0) -
            (b.calculated_price?.calculated_amount ?? 0),
        )[0];

  const calcPrice = variant?.calculated_price;

  const fullItem: GA4Item = {
    item_id: product.id ?? '',
    item_name: product.title ?? '',
    item_brand: product.collection?.title ?? undefined,
    item_category: product.categories?.[0]?.name ?? undefined,
    item_variant: variant?.title ?? undefined,
    price: calcPrice?.calculated_amount ?? undefined,
    currency: calcPrice?.currency_code ?? undefined,
    discount:
      calcPrice?.original_amount != null && calcPrice?.calculated_amount != null
        ? Math.max(0, calcPrice.original_amount - calcPrice.calculated_amount)
        : undefined,
    quantity: 1,
    index: opts?.index,
    item_list_id: opts?.listId,
    item_list_name: opts?.listName,
  };

  return fullItem;
};

type LineItemInput = {
  id: string;
  quantity: number;
  product_id?: string | null;
  variant_id?: string | null;
  product_title?: string | null;
  variant_title?: string | null;
  unit_price?: number | null;
};

export const lineItemToGA4Item = (
  item: LineItemInput,
  opts?: { listId?: string; listName?: string },
): GA4Item => {
  const fullItem: GA4Item = {
    item_id: item.product_id ?? item.variant_id ?? item.id,
    item_name: item.product_title ?? '',
    item_variant: item.variant_title ?? undefined,
    price: item.unit_price ?? undefined,
    quantity: item.quantity,
    item_list_id: opts?.listId,
    item_list_name: opts?.listName,
  };

  return fullItem;
};

export const orderLineItemToGA4Item = (
  item: HttpTypes.StoreOrderLineItem,
): GA4Item => {
  const fullItem: GA4Item = {
    item_id: item.product_id ?? item.variant_id ?? item.id,
    item_name: item.product_title ?? '',
    item_variant: item.variant_title ?? undefined,
    price: item.unit_price,
    quantity: item.quantity,
  };

  return fullItem;
};
